/**
 * YouTubeUploadTab — YouTube Data API v3 publish flow.
 *
 * Layout:
 *   1. YouTubeOAuthButton  — connect / disconnect channel
 *   2. YouTubeQuotaWidget  — quota bar (only when connected)
 *   3. YouTubePublishForm  — submit a new job (only when connected + no active job)
 *      OR
 *      YouTubePublishProgress — SSE+poll view of an in-flight job
 *
 * Receives the preset parked by ResultDisplay (pendingPublishPreset) when the
 * user clicks "Publish on YouTube" after a pipeline generation, whether this tab
 * was already mounted or mounts because of that click, and forwards it to
 * YouTubePublishForm.
 */

import { useState, useEffect } from 'react';
import { YouTubeOAuthButton } from '@/components/youtube-publisher/YouTubeOAuthButton';
import { YouTubeQuotaWidget } from '@/components/youtube-publisher/YouTubeQuotaWidget';
import { YouTubePublishForm } from '@/components/youtube-publisher/YouTubePublishForm';
import { YouTubePublishProgress } from '@/components/youtube-publisher/YouTubePublishProgress';
import type { YouTubePublishPreset } from '@/components/youtube-publisher/types';
import {
  clearPendingPublishPreset,
  peekPendingPublishPreset,
  subscribePendingPublishPreset,
} from '@/components/youtube-publisher/pendingPublishPreset';
import type { YouTubeProfile, YouTubePublishMode } from '@/services/youtubePublishApi';

// YouTubePublishPreset now lives with the youtube-publisher components (the form
// consumes it). Re-exported here for backwards compatibility with any importer
// that referenced it via this page (refactorFable5 Stage 05, finding #10).
export type { YouTubePublishPreset };

export function YouTubeUploadTab() {
  const [currentProfile, setCurrentProfile] = useState<YouTubeProfile | null>(null);
  const [activeJobId, setActiveJobId] = useState<string | null>(null);
  const [preset, setPreset] = useState<YouTubePublishPreset | null>(peekPendingPublishPreset);
  // Owned here (not local to YouTubePublishForm) so it survives the form
  // unmount/remount that happens when a job starts/finishes below -- otherwise
  // a completed "Edit Existing Video" job bounced the user back to "Upload New
  // Video" because the remounted form re-initialized to its default mode.
  const [mode, setMode] = useState<YouTubePublishMode>('end_to_end');

  useEffect(() => {
    // The preset read at mount (initial state) is now ours: drop it from the
    // store so a later visit to this tab does not re-apply a stale one.
    clearPendingPublishPreset();
    return subscribePendingPublishPreset(() => {
      const next = peekPendingPublishPreset();
      if (next) {
        setPreset(next);
        clearPendingPublishPreset();
        // Reset any in-flight job so the form is shown
        setActiveJobId(null);
      }
    });
  }, []);

  return (
    <div className="space-y-6 p-4 sm:p-6">
      <YouTubeOAuthButton onProfileChange={setCurrentProfile} />

      {currentProfile && (
        <>
          <YouTubeQuotaWidget channel_id={currentProfile.channel_id} />

          {activeJobId === null ? (
            <YouTubePublishForm
              channel_id={currentProfile.channel_id}
              onJobStarted={setActiveJobId}
              preset={preset}
              mode={mode}
              onModeChange={setMode}
            />
          ) : (
            <YouTubePublishProgress jobId={activeJobId} onDone={() => setActiveJobId(null)} />
          )}
        </>
      )}
    </div>
  );
}
